import * as THREE from 'three';
import { Game } from './engine/Game';
import { bus } from './engine/EventBus';
import { save } from './engine/SaveSystem';
import { World } from './world/World';
import { Landmarks } from './world/Landmarks';
import { Secrets } from './world/Secrets';
import { Player } from './player/Player';
import { Controller } from './player/Controller';
import { FollowCamera } from './player/FollowCamera';
import { BuildMode } from './house/BuildMode';
import { HUD } from './ui/HUD';
import { MainMenu } from './ui/MainMenu';
import { CustomizationPanel } from './ui/CustomizationPanel';
import { BuildPalette } from './ui/BuildPalette';
import { ShopPanel } from './ui/ShopPanel';
import { PauseMenu } from './ui/PauseMenu';
import { Toast } from './ui/Toast';

const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
const uiRoot = document.getElementById('ui-root') as HTMLElement;

const game = new Game(canvas);

// World
const world = new World(game);
game.register(world);

const landmarks = new Landmarks(world.terrain);
game.scene.add(landmarks.group);

const secrets = new Secrets();
game.scene.add(secrets.group);
game.register(secrets);

// Player
const player = new Player(save.data.character);
game.scene.add(player.root);

const followCam = new FollowCamera(game.camera, game.input);
const controller = new Controller(game.input, world.terrain, player, followCam, () => save.data.character);
// Spawn in front of player house
controller.position.set(landmarks.homePlot.x, world.terrain.heightAt(landmarks.homePlot.x, landmarks.homePlot.z + 6), landmarks.homePlot.z + 6);
player.root.position.copy(controller.position);

bus.on('character:changed', () => {
  player.applyType(save.data.character);
  player.recolor(save.data.character);
});

// Build mode
const build = new BuildMode(game, game.input, landmarks.homePlot);
game.scene.add(build.container);
game.register(build);

// UI
new Toast(uiRoot);
const hud = new HUD(uiRoot, secrets.total);
const mainMenu = new MainMenu(uiRoot);
const customization = new CustomizationPanel(uiRoot);
const buildPalette = new BuildPalette(uiRoot);
const shopPanel = new ShopPanel(uiRoot);
const pauseMenu = new PauseMenu(uiRoot);

hud.show(false);

// -------- Mode wiring --------

function enterPlay(): void {
  mainMenu.hide();
  customization.hide();
  shopPanel.hide();
  pauseMenu.hide();
  buildPalette.hide();
  hud.show(true);
  game.setMode('play');
  game.input.requestPointerLock();
  save.markPlayed();
}

function enterTitle(): void {
  game.setMode('title');
  mainMenu.show();
  customization.hide();
  shopPanel.hide();
  pauseMenu.hide();
  buildPalette.hide();
  hud.show(false);
  game.input.exitPointerLock();
  game.input.suspend(true);
}

function enterCustomize(fromMenu: boolean): void {
  customization.show(!fromMenu);
  game.setMode('customize');
  game.input.exitPointerLock();
  game.input.suspend(true);
  customization.onConfirm = () => {
    customization.hide();
    game.input.suspend(false);
    enterPlay();
  };
  customization.onCancel = () => {
    customization.hide();
    game.input.suspend(false);
    enterPlay();
  };
}

function enterShop(): void {
  shopPanel.show();
  game.setMode('shop');
  game.input.exitPointerLock();
  game.input.suspend(true);
}
function exitShop(): void {
  shopPanel.hide();
  game.input.suspend(false);
  enterPlay();
}
shopPanel.onClose = exitShop;

function enterPause(): void {
  pauseMenu.show();
  game.setMode('menu');
  game.input.exitPointerLock();
  game.input.suspend(true);
}
pauseMenu.onResume = enterPlay;
pauseMenu.onCustomize = () => { pauseMenu.hide(); enterCustomize(false); };
pauseMenu.onQuitToTitle = () => {
  pauseMenu.hide();
  enterTitle();
};

function enterBuild(): void {
  build.enter();
  buildPalette.show();
  game.input.exitPointerLock();
  // Build mode uses cursor, not pointer lock
}
function exitBuild(): void {
  build.exit();
  buildPalette.hide();
  enterPlay();
}
buildPalette.onSelect = (id) => build.selectItem(id);
buildPalette.onExit = exitBuild;

// Menu callbacks
mainMenu.onNewGame = () => { save.reset(); player.applyType(save.data.character); player.recolor(save.data.character); enterCustomize(true); };
mainMenu.onContinue = () => { player.applyType(save.data.character); player.recolor(save.data.character); enterPlay(); };

// Click canvas to relock pointer when playing
canvas.addEventListener('click', () => {
  if (game.mode === 'play' && !game.input.isLocked()) {
    game.input.requestPointerLock();
  }
});

// -------- Main play-loop logic as a Tickable --------

let lastHint: string | null = null;
function setHint(text: string | null) {
  if (text !== lastHint) {
    bus.emit('hint:set', { text });
    lastHint = text;
  }
}

const playLoop = {
  tick: (dt: number) => {
    const mode = game.mode;

    // Esc opens pause (from play) / closes overlays
    if (game.input.consumePress('escape')) {
      if (mode === 'play') enterPause();
      else if (mode === 'menu') enterPlay();
      else if (mode === 'shop') exitShop();
      else if (mode === 'build') exitBuild();
      else if (mode === 'customize' && save.data.hasPlayed) {
        customization.hide();
        game.input.suspend(false);
        enterPlay();
      }
    }

    if (mode === 'play') {
      controller.tick(dt);
      followCam.tick(dt, controller.position);

      secrets.checkCollect(controller.position);

      // Landmark interaction hint
      const near = landmarks.nearest(controller.position);
      if (near) {
        setHint(`Press E to visit ${near.name}`);
        if (game.input.consumePress('e')) {
          near.onInteract();
        }
      } else {
        setHint(null);
      }

      // Build mode toggle on home plot
      if (game.input.consumePress('b')) {
        if (landmarks.isOnHomePlot(controller.position)) {
          enterBuild();
        } else {
          bus.emit('toast', { message: '🏡 Stand inside your cottage to build!' });
        }
      }
    } else if (mode === 'build') {
      // keep camera gently drifting around player
      followCam.tick(dt, controller.position, 2.5);
    } else {
      // In menus: orbit the camera slowly for a nice parallax
      const t = performance.now() * 0.0002;
      game.camera.position.set(
        controller.position.x + Math.sin(t) * 10,
        controller.position.y + 4,
        controller.position.z + Math.cos(t) * 10,
      );
      game.camera.lookAt(controller.position.x, controller.position.y + 1, controller.position.z);
    }
  },
};
game.register(playLoop);

// Flush save on unload for safety
window.addEventListener('beforeunload', () => save.flush());

// Boot
enterTitle();
game.start();

// Helpful console hello
console.log('%c🧚 Fairy Love loaded — welcome!', 'color:#ff7aa8;font-weight:bold;font-size:14px');

// Silence unused warning
void THREE;
